import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, of, takeUntil } from "rxjs";
import { Store } from "@ngrx/store";
import { AppStateInterface } from "../appState.interface";
import {
  invokeMainDashboard,
  setMainDashboard,
  invokeProfileDetails,
  setProfileDetails,
  invokeRegister,
  registerSuccess,
  getAllAdvertiserByFilter,
  invokeApproveAdvertiser,
  approveAdvertiserByEmail,
  invokeConfirmEmail,
  confirmEmailForRegistrationSuccess,
  invokeAdvertiserByAdHashedId,
  advertiserByAdHashedId,
  invokeDeleteAdvertiser,
  invokeUpdateAdvertiser,
  invokeIdIberis,
  resultIdIberis,
  confirmEmailForRegistrationFailed,
  regenerateConfirmRegestrationByToken,
  regenerateConfirmRegestrationSuccess,
  regenerateConfirmRegestrationFailed,
  invokeAllActivatedAdvertisers,
  resultAllActivatedAdvertisers,
  invokeSendVerificationEmail,
  invokeAdvertiserFilter,
  resultSuccess,
} from "./advertiser.actions";
import { AdvertiserService } from "src/app/shared/services/advertiser.service";
import { invokeApiDistroyed, setAPIStatus } from "../apiState.interface";
import { AdvertiserDashboard } from "src/app/shared/models/advertiser/advertiser-dashboard.interface";
import { AdvertiserDetailsInterface } from "src/app/shared/models/advertiser/profile.interface";
import { SuccessDto } from "src/app/shared/models/success-dto";
import { ResultFilterAdvertiser } from "src/app/shared/models/advertiser/result-filter-advertiser.interface";

@Injectable()
export class AdvertiserEffect {
  constructor(
    private actions$: Actions,
    private advertiserService: AdvertiserService,
    private store: Store<AppStateInterface>
  ) {}

  invokeRegistre$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeRegister),
      mergeMap((state) => {
        return this.advertiserService.register(state.advertiser).pipe(
          map((data: SuccessDto | null) =>
            registerSuccess({ successMessage: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeConfirmEmailForRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeConfirmEmail),
      mergeMap((state) => {
        return this.advertiserService
          .confirmEmailForRegistration(state.token)
          .pipe(
            map((data: any | null) =>
              confirmEmailForRegistrationSuccess({ successMessage: data })
            ),
            catchError((error) =>
              of(
                confirmEmailForRegistrationFailed({
                  result: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  regenerateConfirmRegestrationByToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(regenerateConfirmRegestrationByToken),
      mergeMap((state) => {
        return this.advertiserService
          .regenerateConfirmRegestrationByToken(state.token)
          .pipe(
            map((data: any | null) =>
              regenerateConfirmRegestrationSuccess({ successMessage: data })
            ),
            catchError((error) =>
              of(
                regenerateConfirmRegestrationFailed({
                  result: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeMainDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeMainDashboard),
      mergeMap((state) => {
        return this.advertiserService
          .getMainDashboard(state.isRefreshCache)
          .pipe(
            map((data: AdvertiserDashboard | null) =>
              setMainDashboard({ dashboardAdvertiser: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeProfileDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeProfileDetails),
      mergeMap((state) => {
        return this.advertiserService.getProfileDetails(state.email).pipe(
          map((data: AdvertiserDetailsInterface | null) =>
            setProfileDetails({ profileDetails: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeResultFilterAdvertiser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeAdvertiserFilter),
      mergeMap((data) => {
        return this.advertiserService.getAdvertiserByFilter(data.filter).pipe(
          map((data: ResultFilterAdvertiser) =>
            getAllAdvertiserByFilter({ resultFilterAdvertiser: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          ),
          takeUntil(this.actions$.pipe(ofType(invokeApiDistroyed)))
        );
      })
    )
  );

  invokeApproveAdvertiser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeApproveAdvertiser),
      mergeMap((state) => {
        return this.advertiserService.updateAdvertiserStatus(state.dto).pipe(
          map((data: SuccessDto | null) =>
            approveAdvertiserByEmail({ successMessage: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeAdvertiserByAdTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeAdvertiserByAdHashedId),
      mergeMap((state) => {
        return this.advertiserService
          .advertiserByAdHashedId(state.campaignHashedId)
          .pipe(
            map((data: AdvertiserDetailsInterface | null) =>
              advertiserByAdHashedId({ profileDetails: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );
  invokeDeleteAdvertiser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeDeleteAdvertiser),
      mergeMap((state) => {
        return this.advertiserService.deleteAdvertiser(state.email).pipe(
          map((data: SuccessDto | null) =>
            resultSuccess({ successMessage: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );
  invokeSendVerificationEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeSendVerificationEmail),
      mergeMap((state) => {
        return this.advertiserService
          .regenerateConfirmRegestrationByEmail(state.email)
          .pipe(
            map((data: SuccessDto | null) =>
              resultSuccess({ successMessage: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );
  invokeUpdateAdvertiser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeUpdateAdvertiser),
      mergeMap((state) => {
        return this.advertiserService
          .updateAdvertiser(state.email, state.profileDetails)
          .pipe(
            map((data: SuccessDto | null) =>
              resultSuccess({ successMessage: data })
            ),
            catchError((error) =>
              of(
                setAPIStatus({
                  apiStatus: {
                    apiResponseMessage: error,
                    apiStatus: "error",
                  },
                })
              )
            )
          );
      })
    )
  );

  invokeIdIberis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeIdIberis),
      mergeMap((state) => {
        return this.advertiserService.getIdIberis(state.email).pipe(
          map((data: string) => resultIdIberis({ idIberis: data })),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );

  invokeActivatedAdvertisers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invokeAllActivatedAdvertisers),
      mergeMap((state) => {
        return this.advertiserService.getActivatedAdvertisers().pipe(
          map((data: AdvertiserDetailsInterface[] | null) =>
            resultAllActivatedAdvertisers({ publishers: data })
          ),
          catchError((error) =>
            of(
              setAPIStatus({
                apiStatus: {
                  apiResponseMessage: error,
                  apiStatus: "error",
                },
              })
            )
          )
        );
      })
    )
  );
}

